import { useState } from "react";
import { ScrollReveal } from "@/components/animations/ScrollReveal";
import { StaggerContainer, StaggerItem } from "@/components/animations/StaggerContainer";
import { motion } from "motion/react";

const FORM_URL = "https://readdy.ai/api/form/d8a994jvdj2iekufqe4g";

export default function ContactFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    culture: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const cultures = [
    "Soja",
    "Milho",
    "Cana-de-Açúcar",
    "Café",
    "Feijão",
    "Citrus",
    "Outra",
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-28 bg-llk-cream-2">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center mb-8 sm:mb-10 md:mb-14">
          <ScrollReveal>
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-llk-gold-dark mb-3 sm:mb-4">
              FALE COM A GENTE
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-llk-dark font-semibold mb-3 sm:mb-4">
              Solicite uma <span className="italic text-llk-earth">consultoria técnica</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-llk-dark/60 text-sm sm:text-base md:text-lg max-w-xl mx-auto px-2">
              Preencha seus dados e nossa equipe especializada entrará em contato com a melhor estratégia biológica para o seu campo.
            </p>
          </ScrollReveal>
        </div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-llk-earth-pale/40 p-6 sm:p-8 md:p-12 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-llk-green-soft/10 flex items-center justify-center mx-auto mb-4 sm:mb-5"
            >
              <i className="ri-check-double-line text-2xl sm:text-3xl text-llk-green-soft" />
            </motion.div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-llk-dark mb-2 sm:mb-3">
              Mensagem enviada com sucesso!
            </h3>
            <p className="text-llk-dark/60 text-sm sm:text-base">
              Nossa equipe técnica entrará em contato em breve. Agradecemos seu interesse nas soluções LLK Bio Solutions.
            </p>
          </motion.div>
        ) : (
          <ScrollReveal delay={0.3}>
            <form
              id="llk-contact-form"
              data-readdy-form
              action={FORM_URL}
              method="POST"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-llk-earth-pale/40 p-5 sm:p-6 md:p-10 shadow-sm"
            >
              <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6" staggerDelay={0.06} delayChildren={0.1}>
                {/* Nome */}
                <StaggerItem>
                  <div>
                    <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-llk-dark mb-1.5 sm:mb-2">
                      Nome completo <span className="text-llk-gold">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-llk-earth-pale/60 bg-llk-cream text-llk-dark text-sm placeholder:text-llk-dark/30 focus:outline-none focus:border-llk-gold focus:ring-1 focus:ring-llk-gold/20 transition-all"
                    />
                  </div>
                </StaggerItem>

                {/* Email */}
                <StaggerItem>
                  <div>
                    <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-llk-dark mb-1.5 sm:mb-2">
                      E-mail <span className="text-llk-gold">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-llk-earth-pale/60 bg-llk-cream text-llk-dark text-sm placeholder:text-llk-dark/30 focus:outline-none focus:border-llk-gold focus:ring-1 focus:ring-llk-gold/20 transition-all"
                    />
                  </div>
                </StaggerItem>

                {/* Telefone */}
                <StaggerItem>
                  <div>
                    <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-llk-dark mb-1.5 sm:mb-2">
                      WhatsApp / Telefone <span className="text-llk-gold">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(00) 00000-0000"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-llk-earth-pale/60 bg-llk-cream text-llk-dark text-sm placeholder:text-llk-dark/30 focus:outline-none focus:border-llk-gold focus:ring-1 focus:ring-llk-gold/20 transition-all"
                    />
                  </div>
                </StaggerItem>

                {/* Cultura */}
                <StaggerItem>
                  <div>
                    <label htmlFor="culture" className="block text-xs sm:text-sm font-medium text-llk-dark mb-1.5 sm:mb-2">
                      Cultura de interesse
                    </label>
                    <select
                      id="culture"
                      name="culture"
                      value={formData.culture}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-llk-earth-pale/60 bg-llk-cream text-llk-dark text-sm focus:outline-none focus:border-llk-gold focus:ring-1 focus:ring-llk-gold/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Selecione uma cultura</option>
                      {cultures.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </StaggerItem>
              </StaggerContainer>

              {/* Mensagem */}
              <ScrollReveal delay={0.4}>
                <div className="mt-4 sm:mt-5 md:mt-6">
                  <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-llk-dark mb-1.5 sm:mb-2">
                    Observações / Mensagem
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Conte-nos sobre sua propriedade, área, desafios ou o que busca..."
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-llk-earth-pale/60 bg-llk-cream text-llk-dark text-sm placeholder:text-llk-dark/30 focus:outline-none focus:border-llk-gold focus:ring-1 focus:ring-llk-gold/20 transition-all resize-none"
                  />
                  <p className="text-right text-[11px] sm:text-xs text-llk-dark/40 mt-1">
                    {formData.message.length}/500
                  </p>
                </div>
              </ScrollReveal>

              {/* Submit */}
              <ScrollReveal delay={0.5}>
                <div className="mt-5 sm:mt-6 md:mt-8">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 bg-llk-dark text-white text-sm font-semibold rounded-xl hover:bg-llk-gold transition-colors duration-300 cursor-pointer whitespace-normal sm:whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line" />
                    Enviar solicitação de contato
                  </motion.button>
                </div>
              </ScrollReveal>

              <p className="text-center text-[11px] sm:text-xs text-llk-dark/40 mt-3 sm:mt-4">
                Seus dados são protegidos e usados apenas para contato consultivo LLK.
              </p>
            </form>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}